"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from "@/components/ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { ChatStatus, FileUIPart } from "ai";
import { CornerDownLeftIcon, ImageIcon, Loader2Icon, MicIcon, PaperclipIcon, PlusIcon, SquareIcon, XIcon } from "lucide-react";
import { nanoid } from "nanoid";
import Image from "next/image";
import { type ChangeEvent, type ChangeEventHandler, Children, type ClipboardEventHandler, type ComponentProps, createContext, Fragment, type HTMLAttributes, type KeyboardEventHandler, type PropsWithChildren, type ReactNode, type RefObject, type SubmitEvent, type SubmitEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// ============================================================================
// Provider Context & Types
// ============================================================================

export interface AttachmentsContext {
  files: (FileUIPart & {
    id: string;
  })[];
  add: (files: File[] | FileList) => void;
  remove: (id: string) => void;
  clear: () => void;
  openFileDialog: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
}
export interface TextInputContext {
  value: string;
  setInput: (v: string) => void;
  clear: () => void;
}
export interface PromptInputControllerProps {
  textInput: TextInputContext;
  attachments: AttachmentsContext;
  /** INTERNAL: Allows PromptInput to register its file textInput + "open" callback */
  __registerFileInput: (ref: RefObject<HTMLInputElement | null>, open: () => void) => void;
}
const PromptInputController = createContext<PromptInputControllerProps | null>(null);
const ProviderAttachmentsContext = createContext<AttachmentsContext | null>(null);
export const usePromptInputController = () => {
  const ctx = useContext(PromptInputController);
  if (!ctx) {
    throw new Error("Wrap your component inside <PromptInputProvider> to use usePromptInputController().");
  }
  return ctx;
};

// Optional variants (do NOT throw). Useful for dual-mode components.
const useOptionalPromptInputController = () => {
  return useContext(PromptInputController);
};
export const useProviderAttachments = () => {
  const ctx = useContext(ProviderAttachmentsContext);
  if (!ctx) {
    throw new Error("Wrap your component inside <PromptInputProvider> to use useProviderAttachments().");
  }
  return ctx;
};
const useOptionalProviderAttachments = () => {
  return useContext(ProviderAttachmentsContext);
};
export type PromptInputProviderProps = PropsWithChildren<{
  initialInput?: string;
}>;

/**
 * Optional global provider that lifts PromptInput state outside of PromptInput.
 * If you don't use it, PromptInput stays fully self-managed.
 */
export function PromptInputProvider(t0) {
  const $ = _c(25);
  const {
    initialInput: t1,
    children
  } = t0;
  const initialTextInput = t1 === undefined ? "" : t1;
  const [textInput, setTextInput] = useState(initialTextInput);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setTextInput("");
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const clearInput = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [attachmentFiles, setAttachmentFiles] = useState(t3);
  const fileInputRef = useRef(null);
  const openRef = useRef(_temp);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = files => {
      const incoming = Array.from(files);
      if (incoming.length === 0) {
        return;
      }
      setAttachmentFiles(prev => prev.concat(incoming.map(_temp2)));
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const add = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = id => {
      setAttachmentFiles(prev_0 => {
        const found = prev_0.find(f => f.id === id);
        if (found?.url) {
          URL.revokeObjectURL(found.url);
        }
        return prev_0.filter(f_0 => f_0.id !== id);
      });
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const remove = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setAttachmentFiles(_temp3);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const clear = t6;
  const attachmentsRef = useRef(attachmentFiles);
  let t7;
  let t8;
  if ($[5] !== attachmentFiles) {
    t7 = () => {
      attachmentsRef.current = attachmentFiles;
    };
    t8 = [attachmentFiles];
    $[5] = attachmentFiles;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => () => {
      for (const f_2 of attachmentsRef.current) {
        if (f_2.url) {
          URL.revokeObjectURL(f_2.url);
        }
      }
    };
    t10 = [];
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      openRef.current();
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  const openFileDialog = t11;
  let t12;
  if ($[11] !== attachmentFiles) {
    t12 = {
      files: attachmentFiles,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef
    };
    $[11] = attachmentFiles;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const attachments = t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (ref, open) => {
      fileInputRef.current = ref.current;
      openRef.current = open;
    };
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const __registerFileInput = t13;
  let t14;
  if ($[14] !== textInput) {
    t14 = {
      value: textInput,
      setInput: setTextInput,
      clear: clearInput
    };
    $[14] = textInput;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== attachments || $[17] !== t14) {
    t15 = {
      textInput: t14,
      attachments,
      __registerFileInput
    };
    $[16] = attachments;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const controller = t15;
  let t16;
  if ($[19] !== attachments || $[20] !== children) {
    t16 = <ProviderAttachmentsContext.Provider value={attachments}>{children}</ProviderAttachmentsContext.Provider>;
    $[19] = attachments;
    $[20] = children;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== controller || $[23] !== t16) {
    t17 = <PromptInputController.Provider value={controller}>{t16}</PromptInputController.Provider>;
    $[22] = controller;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  return t17;
}

// ============================================================================
// Component Context & Hooks
// ============================================================================
function _temp3(prev_1) {
  for (const f_1 of prev_1) {
    if (f_1.url) {
      URL.revokeObjectURL(f_1.url);
    }
  }
  return [];
}
function _temp2(file) {
  return {
    id: nanoid(),
    type: "file" as const,
    url: URL.createObjectURL(file),
    mediaType: file.type,
    filename: file.name
  };
}
function _temp() {}
const LocalAttachmentsContext = createContext<AttachmentsContext | null>(null);
export const usePromptInputAttachments = () => {
  const provider = useOptionalProviderAttachments();
  const local = useContext(LocalAttachmentsContext);
  const context = provider ?? local;
  if (!context) {
    throw new Error("usePromptInputAttachments must be used within a PromptInput or PromptInputProvider");
  }
  return context;
};
export type PromptInputAttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: FileUIPart & {
    id: string;
  };
  className?: string;
};
export function PromptInputAttachment(t0) {
  const $ = _c(45);
  let className;
  let data;
  let props;
  if ($[0] !== t0) {
    ({
      data,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = data;
    $[3] = props;
  } else {
    className = $[1];
    data = $[2];
    props = $[3];
  }
  const attachments = usePromptInputAttachments();
  const filename = data.filename ?? "";
  const mediaType = data.mediaType.startsWith("image/") && data.url ? "image" : "file";
  const isImage = mediaType === "image";
  const attachmentLabel = filename || (isImage ? "Image" : "Attachment");
  let t1;
  if ($[4] !== className) {
    t1 = cn("group border-border hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 relative flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-1.5 text-sm font-medium transition-all select-none", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== data.url || $[7] !== filename || $[8] !== isImage) {
    t2 = <div className="bg-background absolute inset-0 flex size-5 items-center justify-center overflow-hidden rounded transition-opacity group-hover:opacity-0">{isImage ? <Image alt={filename || "attachment"} className="size-5 object-cover" height={20} src={data.url} width={20} unoptimized={true} /> : <div className="text-muted-foreground flex size-5 items-center justify-center"><PaperclipIcon className="size-3" /></div>}</div>;
    $[6] = data.url;
    $[7] = filename;
    $[8] = isImage;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== attachments || $[11] !== data.id) {
    t3 = e => {
      e.stopPropagation();
      attachments.remove(data.id);
    };
    $[10] = attachments;
    $[11] = data.id;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <XIcon />;
    t5 = <span className="sr-only">Remove</span>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3) {
    t6 = <Button aria-label="Remove attachment" className="absolute inset-0 size-5 cursor-pointer rounded p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 [&>svg]:size-2.5" onClick={t3} type="button" variant="ghost">{t4}{t5}</Button>;
    $[15] = t3;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t2 || $[18] !== t6) {
    t7 = <div className="relative size-5 shrink-0">{t2}{t6}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== attachmentLabel) {
    t8 = <span className="flex-1 truncate">{attachmentLabel}</span>;
    $[20] = attachmentLabel;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== data.id || $[23] !== props || $[24] !== t1 || $[25] !== t7 || $[26] !== t8) {
    t9 = <HoverCardTrigger asChild={true}><div className={t1} key={data.id} {...props}>{t7}{t8}</div></HoverCardTrigger>;
    $[22] = data.id;
    $[23] = props;
    $[24] = t1;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== data.url || $[29] !== filename || $[30] !== isImage) {
    t10 = isImage && <div className="flex max-h-96 w-96 items-center justify-center overflow-hidden rounded-md border"><Image alt={filename || "attachment preview"} className="max-h-full max-w-full object-contain" height={384} src={data.url} width={448} unoptimized={true} /></div>;
    $[28] = data.url;
    $[29] = filename;
    $[30] = isImage;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = filename || (isImage ? "Image" : "Attachment");
  let t12;
  if ($[32] !== t11) {
    t12 = <h4 className="truncate text-sm leading-none font-semibold">{t11}</h4>;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== data.mediaType) {
    t13 = data.mediaType && <p className="text-muted-foreground truncate font-mono text-xs">{data.mediaType}</p>;
    $[34] = data.mediaType;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = <div className="flex items-center gap-2.5"><div className="min-w-0 flex-1 space-y-1 px-0.5">{t12}{t13}</div></div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t10 || $[40] !== t14) {
    t15 = <PromptInputHoverCardContent className="w-auto p-2"><div className="w-auto space-y-3">{t10}{t14}</div></PromptInputHoverCardContent>;
    $[39] = t10;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t15 || $[43] !== t9) {
    t16 = <PromptInputHoverCard>{t9}{t15}</PromptInputHoverCard>;
    $[42] = t15;
    $[43] = t9;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
export type PromptInputAttachmentsProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  children: (attachment: FileUIPart & {
    id: string;
  }) => ReactNode;
};
export function PromptInputAttachments(t0) {
  const $ = _c(15);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const attachments = usePromptInputAttachments();
  if (!attachments.files.length) {
    return null;
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex w-full flex-wrap items-center gap-2 p-3", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== attachments.files || $[7] !== children) {
    let t3;
    if ($[9] !== children) {
      t3 = file => <Fragment key={file.id}>{children(file)}</Fragment>;
      $[9] = children;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = attachments.files.map(t3);
    $[6] = attachments.files;
    $[7] = children;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[11] !== props || $[12] !== t1 || $[13] !== t2) {
    t3 = <div className={t1} {...props}>{t2}</div>;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
export type PromptInputActionAddAttachmentsProps = ComponentProps<typeof DropdownMenuItem> & {
  label?: string;
};
export const PromptInputActionAddAttachments = t0 => {
  const $ = _c(10);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const label = t1 === undefined ? "Add photos or files" : t1;
  const attachments = usePromptInputAttachments();
  let t2;
  if ($[3] !== attachments) {
    t2 = e => {
      e.preventDefault();
      attachments.openFileDialog();
    };
    $[3] = attachments;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ImageIcon className="mr-2 size-4" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label || $[7] !== props || $[8] !== t2) {
    t4 = <DropdownMenuItem {...props} onSelect={t2}>{t3} {label}</DropdownMenuItem>;
    $[6] = label;
    $[7] = props;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
export interface PromptInputMessage {
  text: string;
  files: FileUIPart[];
}
export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, "onSubmit" | "onError"> & {
  accept?: string; // e.g., "image/*" or leave undefined for any
  multiple?: boolean;
  // When true, accepts drops anywhere on document. Default false (opt-in).
  globalDrop?: boolean;
  // Render a hidden input with given name and keep it in sync for native form posts. Default false.
  syncHiddenInput?: boolean;
  // Minimal constraints
  maxFiles?: number;
  maxFileSize?: number; // bytes
  onError?: (err: {
    code: "max_files" | "max_file_size" | "accept";
    message: string;
  }) => void;
  onSubmit: (message: PromptInputMessage, event: SubmitEvent<HTMLFormElement>) => void | Promise<void>;
};
export const PromptInput = t0 => {
  const $ = _c(83);
  let accept;
  let children;
  let className;
  let globalDrop;
  let maxFileSize;
  let maxFiles;
  let multiple;
  let onError;
  let onSubmit;
  let props;
  let syncHiddenInput;
  if ($[0] !== t0) {
    ({
      className,
      accept,
      multiple,
      globalDrop,
      syncHiddenInput,
      maxFiles,
      maxFileSize,
      onError,
      onSubmit,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = accept;
    $[2] = children;
    $[3] = className;
    $[4] = globalDrop;
    $[5] = maxFileSize;
    $[6] = maxFiles;
    $[7] = multiple;
    $[8] = onError;
    $[9] = onSubmit;
    $[10] = props;
    $[11] = syncHiddenInput;
  } else {
    accept = $[1];
    children = $[2];
    className = $[3];
    globalDrop = $[4];
    maxFileSize = $[5];
    maxFiles = $[6];
    multiple = $[7];
    onError = $[8];
    onSubmit = $[9];
    props = $[10];
    syncHiddenInput = $[11];
  }
  const controller = useOptionalPromptInputController();
  const usingProvider = !!controller;
  const inputRef = useRef(null);
  const formRef = useRef(null);
  let t1;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const [items, setItems] = useState(t1);
  const files = usingProvider ? controller.attachments.files : items;
  const filesRef = useRef(files);
  let t2;
  let t3;
  if ($[13] !== files) {
    t2 = () => {
      filesRef.current = files;
    };
    t3 = [files];
    $[13] = files;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  useEffect(t2, t3);
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      inputRef.current?.click();
    };
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const openFileDialogLocal = t4;
  let t5;
  if ($[17] !== accept) {
    t5 = f => {
      if (!accept || accept.trim() === "") {
        return true;
      }
      const patterns = accept.split(",").map(_temp4).filter(Boolean);
      return patterns.some(pattern => {
        if (pattern.endsWith("/*")) {
          const prefix = pattern.slice(0, -1);
          return f.type.startsWith(prefix);
        }
        return f.type === pattern;
      });
    };
    $[17] = accept;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const matchesAccept = t5;
  let t6;
  if ($[19] !== matchesAccept || $[20] !== maxFileSize || $[21] !== maxFiles || $[22] !== onError) {
    t6 = fileList => {
      const incoming = Array.from(fileList);
      const accepted = incoming.filter(f_0 => matchesAccept(f_0));
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: "accept",
          message: "No files match the accepted types."
        });
        return;
      }
      const withinSize = f_1 => maxFileSize ? f_1.size <= maxFileSize : true;
      const sized = accepted.filter(withinSize);
      if (accepted.length > 0 && sized.length === 0) {
        onError?.({
          code: "max_file_size",
          message: "All files exceed the maximum size."
        });
        return;
      }
      setItems(prev => {
        const capacity = typeof maxFiles === "number" ? Math.max(0, maxFiles - prev.length) : undefined;
        const capped = typeof capacity === "number" ? sized.slice(0, capacity) : sized;
        if (typeof capacity === "number" && sized.length > capacity) {
          onError?.({
            code: "max_files",
            message: "Too many files. Some were not added."
          });
        }
        const next = [];
        for (const file of capped) {
          next.push({
            id: nanoid(),
            type: "file",
            url: URL.createObjectURL(file),
            mediaType: file.type,
            filename: file.name
          });
        }
        return prev.concat(next);
      });
    };
    $[19] = matchesAccept;
    $[20] = maxFileSize;
    $[21] = maxFiles;
    $[22] = onError;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const addLocal = t6;
  let t7;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = id => {
      setItems(prev_0 => {
        const found = prev_0.find(file_0 => file_0.id === id);
        if (found?.url) {
          URL.revokeObjectURL(found.url);
        }
        return prev_0.filter(file_1 => file_1.id !== id);
      });
    };
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const removeLocal = t7;
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setItems(_temp5);
    };
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const clearLocal = t8;
  const add = usingProvider ? controller.attachments.add : addLocal;
  const remove = usingProvider ? controller.attachments.remove : removeLocal;
  const clear = usingProvider ? controller.attachments.clear : clearLocal;
  const openFileDialog = usingProvider ? controller.attachments.openFileDialog : openFileDialogLocal;
  let t10;
  let t9;
  if ($[26] !== controller || $[27] !== usingProvider) {
    t9 = () => {
      if (!usingProvider) {
        return;
      }
      controller.__registerFileInput(inputRef, () => inputRef.current?.click());
    };
    t10 = [usingProvider, controller];
    $[26] = controller;
    $[27] = usingProvider;
    $[28] = t10;
    $[29] = t9;
  } else {
    t10 = $[28];
    t9 = $[29];
  }
  useEffect(t9, t10);
  let t11;
  if ($[30] !== files.length || $[31] !== syncHiddenInput) {
    t11 = () => {
      if (syncHiddenInput && inputRef.current && files.length === 0) {
        inputRef.current.value = "";
      }
    };
    $[30] = files.length;
    $[31] = syncHiddenInput;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== files || $[34] !== syncHiddenInput) {
    t12 = [files, syncHiddenInput];
    $[33] = files;
    $[34] = syncHiddenInput;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[36] !== add || $[37] !== globalDrop) {
    t13 = () => {
      const form = formRef.current;
      if (!form) {
        return;
      }
      if (globalDrop) {
        return;
      }
      const onDragOver = _temp6;
      const onDrop = e_0 => {
        if (e_0.dataTransfer?.types.includes("Files")) {
          e_0.preventDefault();
        }
        if (e_0.dataTransfer?.files && e_0.dataTransfer.files.length > 0) {
          add(e_0.dataTransfer.files);
        }
      };
      form.addEventListener("dragover", onDragOver);
      form.addEventListener("drop", onDrop);
      return () => {
        form.removeEventListener("dragover", onDragOver);
        form.removeEventListener("drop", onDrop);
      };
    };
    t14 = [add, globalDrop];
    $[36] = add;
    $[37] = globalDrop;
    $[38] = t13;
    $[39] = t14;
  } else {
    t13 = $[38];
    t14 = $[39];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[40] !== add || $[41] !== globalDrop) {
    t15 = () => {
      if (!globalDrop) {
        return;
      }
      const onDragOver_0 = _temp7;
      const onDrop_0 = e_2 => {
        if (e_2.dataTransfer?.types.includes("Files")) {
          e_2.preventDefault();
        }
        if (e_2.dataTransfer?.files && e_2.dataTransfer.files.length > 0) {
          add(e_2.dataTransfer.files);
        }
      };
      document.addEventListener("dragover", onDragOver_0);
      document.addEventListener("drop", onDrop_0);
      return () => {
        document.removeEventListener("dragover", onDragOver_0);
        document.removeEventListener("drop", onDrop_0);
      };
    };
    t16 = [add, globalDrop];
    $[40] = add;
    $[41] = globalDrop;
    $[42] = t15;
    $[43] = t16;
  } else {
    t15 = $[42];
    t16 = $[43];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[44] !== usingProvider) {
    t17 = () => () => {
      if (!usingProvider) {
        for (const f_2 of filesRef.current) {
          if (f_2.url) {
            URL.revokeObjectURL(f_2.url);
          }
        }
      }
    };
    t18 = [usingProvider];
    $[44] = usingProvider;
    $[45] = t17;
    $[46] = t18;
  } else {
    t17 = $[45];
    t18 = $[46];
  }
  useEffect(t17, t18);
  let t19;
  if ($[47] !== add) {
    t19 = event => {
      if (event.currentTarget.files) {
        add(event.currentTarget.files);
      }
      event.currentTarget.value = "";
    };
    $[47] = add;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const handleChange = t19;
  const convertBlobUrlToDataUrl = _temp8;
  let t20;
  if ($[49] !== files) {
    t20 = files.map(_temp9);
    $[49] = files;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== add || $[52] !== clear || $[53] !== openFileDialog || $[54] !== remove || $[55] !== t20) {
    t21 = {
      files: t20,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef: inputRef
    };
    $[51] = add;
    $[52] = clear;
    $[53] = openFileDialog;
    $[54] = remove;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  const ctx = t21;
  let t22;
  if ($[57] !== clear || $[58] !== controller || $[59] !== files || $[60] !== onSubmit || $[61] !== usingProvider) {
    t22 = event_0 => {
      event_0.preventDefault();
      const form_0 = event_0.currentTarget;
      const text = usingProvider ? controller.textInput.value : (() => {
        const formData = new FormData(form_0);
        return formData.get("message") as string || "";
      })();
      if (!usingProvider) {
        form_0.reset();
      }
      Promise.all(files.map(async item_0 => {
        if (item_0.url.startsWith("blob:")) {
          const dataUrl = await convertBlobUrlToDataUrl(item_0.url);
          return {
            ...item_0,
            url: dataUrl ?? item_0.url
          };
        }
        return item_0;
      })).then(convertedFiles => {
        try {
          const result = onSubmit({
            text,
            files: convertedFiles
          }, event_0);
          if (result instanceof Promise) {
            result.then(() => {
              clear();
              if (usingProvider) {
                controller.textInput.clear();
              }
            }).catch(_temp10);
          } else {
            clear();
            if (usingProvider) {
              controller.textInput.clear();
            }
          }
        } catch {}
      }).catch(_temp11);
    };
    $[57] = clear;
    $[58] = controller;
    $[59] = files;
    $[60] = onSubmit;
    $[61] = usingProvider;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const handleSubmit = t22;
  let t23;
  if ($[63] !== accept || $[64] !== handleChange || $[65] !== multiple) {
    t23 = <input accept={accept} aria-label="Upload files" className="hidden" multiple={multiple} onChange={handleChange} ref={inputRef} title="Upload files" type="file" />;
    $[63] = accept;
    $[64] = handleChange;
    $[65] = multiple;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== className) {
    t24 = cn("w-full", className);
    $[67] = className;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== children) {
    t25 = <InputGroup className="overflow-hidden">{children}</InputGroup>;
    $[69] = children;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== handleSubmit || $[72] !== props || $[73] !== t24 || $[74] !== t25) {
    t26 = <form className={t24} onSubmit={handleSubmit} ref={formRef} {...props}>{t25}</form>;
    $[71] = handleSubmit;
    $[72] = props;
    $[73] = t24;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t23 || $[77] !== t26) {
    t27 = <>{t23}{t26}</>;
    $[76] = t23;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  const inner = t27;
  let t28;
  if ($[79] !== ctx || $[80] !== inner || $[81] !== usingProvider) {
    t28 = usingProvider ? inner : <LocalAttachmentsContext.Provider value={ctx}>{inner}</LocalAttachmentsContext.Provider>;
    $[79] = ctx;
    $[80] = inner;
    $[81] = usingProvider;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  return t28;
};
export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputBody = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("contents", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTextareaProps = ComponentProps<typeof InputGroupTextarea>;
export const PromptInputTextarea = t0 => {
  const $ = _c(24);
  let className;
  let onChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onChange,
      className,
      placeholder: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChange;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    onChange = $[2];
    props = $[3];
    t1 = $[4];
  }
  const placeholder = t1 === undefined ? "What would you like to know?" : t1;
  const controller = useOptionalPromptInputController();
  const attachments = usePromptInputAttachments();
  const [isComposing, setIsComposing] = useState(false);
  let t2;
  if ($[5] !== attachments || $[6] !== isComposing) {
    t2 = e => {
      if (e.key === "Enter") {
        if (isComposing || e.nativeEvent.isComposing) {
          return;
        }
        if (e.shiftKey) {
          return;
        }
        e.preventDefault();
        const form = e.currentTarget.form;
        const submitButton = form?.querySelector("button[type=\"submit\"]") as HTMLButtonElement | null;
        if (submitButton?.disabled) {
          return;
        }
        form?.requestSubmit();
      }
      if (e.key === "Backspace" && e.currentTarget.value === "" && attachments.files.length > 0) {
        e.preventDefault();
        const lastAttachment = attachments.files.at(-1);
        if (lastAttachment) {
          attachments.remove(lastAttachment.id);
        }
      }
    };
    $[5] = attachments;
    $[6] = isComposing;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[8] !== attachments) {
    t3 = event => {
      const items = event.clipboardData.items;
      const files = [];
      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        attachments.add(files);
      }
    };
    $[8] = attachments;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handlePaste = t3;
  let t4;
  if ($[10] !== controller || $[11] !== onChange) {
    t4 = controller ? {
      value: controller.textInput.value,
      onChange: e_0 => {
        controller.textInput.setInput(e_0.currentTarget.value);
        onChange?.(e_0);
      }
    } : {
      onChange
    };
    $[10] = controller;
    $[11] = onChange;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const controlledProps = t4;
  let t5;
  if ($[13] !== className) {
    t5 = cn("field-sizing-content max-h-48 min-h-16", className);
    $[13] = className;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsComposing(false);
    };
    t7 = () => {
      setIsComposing(true);
    };
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[17] !== controlledProps || $[18] !== handleKeyDown || $[19] !== handlePaste || $[20] !== placeholder || $[21] !== props || $[22] !== t5) {
    t8 = <InputGroupTextarea className={t5} name="message" onCompositionEnd={t6} onCompositionStart={t7} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={placeholder} {...props} {...controlledProps} />;
    $[17] = controlledProps;
    $[18] = handleKeyDown;
    $[19] = handlePaste;
    $[20] = placeholder;
    $[21] = props;
    $[22] = t5;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
export type PromptInputHeaderProps = Omit<ComponentProps<typeof InputGroupAddon>, "align">;
export const PromptInputHeader = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("order-first flex-wrap gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <InputGroupAddon align="block-end" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputFooterProps = Omit<ComponentProps<typeof InputGroupAddon>, "align">;
export const PromptInputFooter = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("justify-between gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <InputGroupAddon align="block-end" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTools = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputButtonProps = ComponentProps<typeof InputGroupButton>;
export const PromptInputButton = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let variant;
  if ($[0] !== t0) {
    const {
      variant: t2,
      className: t3,
      size,
      ...t4
    } = t0;
    className = t3;
    props = t4;
    variant = t2 === undefined ? "ghost" : t2;
    t1 = size ?? (Children.count(props.children) > 1 ? "sm" : "icon-sm");
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = variant;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    variant = $[4];
  }
  const newSize = t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn(className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== newSize || $[8] !== props || $[9] !== t2 || $[10] !== variant) {
    t3 = <InputGroupButton className={t2} size={newSize} type="button" variant={variant} {...props} />;
    $[7] = newSize;
    $[8] = props;
    $[9] = t2;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export type PromptInputActionMenuProps = ComponentProps<typeof DropdownMenu>;
export const PromptInputActionMenu = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <DropdownMenu {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type PromptInputActionMenuTriggerProps = PromptInputButtonProps;
export const PromptInputActionMenuTrigger = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== children) {
    t1 = children ?? <PlusIcon className="size-4" />;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== className || $[7] !== props || $[8] !== t1) {
    t2 = <DropdownMenuTrigger asChild={true}><PromptInputButton className={className} {...props}>{t1}</PromptInputButton></DropdownMenuTrigger>;
    $[6] = className;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export type PromptInputActionMenuContentProps = ComponentProps<typeof DropdownMenuContent>;
export const PromptInputActionMenuContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DropdownMenuContent align="start" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputActionMenuItemProps = ComponentProps<typeof DropdownMenuItem>;
export const PromptInputActionMenuItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DropdownMenuItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};

// Note: Actions that perform side-effects (like opening a file dialog)
// are provided in opt-in modules (e.g., prompt-input-attachments).

export type PromptInputSubmitProps = ComponentProps<typeof InputGroupButton> & {
  status?: ChatStatus;
  onStop?: () => void;
};
export const PromptInputSubmit = t0 => {
  const $ = _c(26);
  let children;
  let className;
  let onStop;
  let props;
  let status;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      size: t2,
      status,
      onStop,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onStop;
    $[4] = props;
    $[5] = status;
    $[6] = t1;
    $[7] = t2;
  } else {
    children = $[1];
    className = $[2];
    onStop = $[3];
    props = $[4];
    status = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "icon-sm" : t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CornerDownLeftIcon className="size-4" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let Icon = t3;
  if (status === "submitted") {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loader2Icon className="size-4 animate-spin" />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    Icon = t4;
  } else {
    if (status === "streaming") {
      let t4;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <SquareIcon className="size-4" />;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      Icon = t4;
    } else {
      if (status === "error") {
        let t4;
        if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <XIcon className="size-4" />;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        Icon = t4;
      }
    }
  }
  let t4;
  if ($[12] !== onStop || $[13] !== status) {
    t4 = () => {
      if (status === "streaming" && onStop) {
        onStop();
      }
    };
    $[12] = onStop;
    $[13] = status;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleClick = t4;
  const t5 = status === "streaming" ? "Stop" : "Submit";
  let t6;
  if ($[15] !== className) {
    t6 = cn(className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = status === "streaming" ? "button" : "submit";
  const t8 = status === "streaming" ? handleClick : undefined;
  const t9 = children ?? Icon;
  let t10;
  if ($[17] !== props || $[18] !== size || $[19] !== t5 || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9 || $[24] !== variant) {
    t10 = <InputGroupButton aria-label={t5} className={t6} size={size} type={t7} variant={variant} onClick={t8} {...props}>{t9}</InputGroupButton>;
    $[17] = props;
    $[18] = size;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = variant;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => unknown) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => unknown) | null;
}
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}
export type PromptInputSpeechButtonProps = ComponentProps<typeof PromptInputButton> & {
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  onTranscriptionChange?: (text: string) => void;
};
export const PromptInputSpeechButton = t0 => {
  const $ = _c(21);
  let className;
  let onTranscriptionChange;
  let props;
  let textareaRef;
  if ($[0] !== t0) {
    ({
      className,
      textareaRef,
      onTranscriptionChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onTranscriptionChange;
    $[3] = props;
    $[4] = textareaRef;
  } else {
    className = $[1];
    onTranscriptionChange = $[2];
    props = $[3];
    textareaRef = $[4];
  }
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(_temp12);
  const recognitionRef = useRef(null);
  let t1;
  let t2;
  if ($[5] !== isSupported || $[6] !== onTranscriptionChange || $[7] !== textareaRef) {
    t1 = () => {
      if (!isSupported) {
        return;
      }
      const SpeechRecognition = window.SpeechRecognition;
      const speechRecognition = new SpeechRecognition();
      speechRecognition.continuous = true;
      speechRecognition.interimResults = true;
      speechRecognition.lang = "en-US";
      speechRecognition.onstart = () => {
        setIsListening(true);
      };
      speechRecognition.onend = () => {
        setIsListening(false);
      };
      speechRecognition.onresult = event => {
        let finalTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result?.isFinal) {
            finalTranscript = finalTranscript + (result[0]?.transcript ?? "");
          }
        }
        if (finalTranscript && textareaRef?.current) {
          const textarea = textareaRef.current;
          const currentValue = textarea.value;
          const newValue = currentValue + (currentValue ? " " : "") + finalTranscript;
          textarea.value = newValue;
          textarea.dispatchEvent(new Event("input", {
            bubbles: true
          }));
          onTranscriptionChange?.(newValue);
        }
      };
      speechRecognition.onerror = event_0 => {
        console.error("Speech recognition error:", event_0.error);
        setIsListening(false);
      };
      recognitionRef.current = speechRecognition;
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    };
    t2 = [isSupported, textareaRef, onTranscriptionChange];
    $[5] = isSupported;
    $[6] = onTranscriptionChange;
    $[7] = textareaRef;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  useEffect(t1, t2);
  let t3;
  if ($[10] !== isListening) {
    t3 = () => {
      if (!recognitionRef.current) {
        return;
      }
      if (isListening) {
        recognitionRef.current.stop();
      } else {
        recognitionRef.current.start();
      }
    };
    $[10] = isListening;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const toggleListening = t3;
  const t4 = isListening && "bg-accent text-accent-foreground animate-pulse";
  let t5;
  if ($[12] !== className || $[13] !== t4) {
    t5 = cn("relative transition-all duration-200", t4, className);
    $[12] = className;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = !isSupported;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MicIcon className="size-4" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== props || $[17] !== t5 || $[18] !== t6 || $[19] !== toggleListening) {
    t8 = <PromptInputButton className={t5} disabled={t6} onClick={toggleListening} {...props}>{t7}</PromptInputButton>;
    $[16] = props;
    $[17] = t5;
    $[18] = t6;
    $[19] = toggleListening;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export type PromptInputSelectProps = ComponentProps<typeof Select>;
export const PromptInputSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Select {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type PromptInputSelectTriggerProps = ComponentProps<typeof SelectTrigger>;
export const PromptInputSelectTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground border-none bg-transparent font-medium shadow-none transition-colors", "hover:bg-accent hover:text-foreground aria-expanded:bg-accent aria-expanded:text-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputSelectContentProps = ComponentProps<typeof SelectContent>;
export const PromptInputSelectContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputSelectItemProps = ComponentProps<typeof SelectItem>;
export const PromptInputSelectItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputSelectValueProps = ComponentProps<typeof SelectValue>;
export const PromptInputSelectValue = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectValue className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputHoverCardProps = ComponentProps<typeof HoverCard>;
export const PromptInputHoverCard = t0 => {
  const $ = _c(8);
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      openDelay: t1,
      closeDelay: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const openDelay = t1 === undefined ? 0 : t1;
  const closeDelay = t2 === undefined ? 0 : t2;
  let t3;
  if ($[4] !== closeDelay || $[5] !== openDelay || $[6] !== props) {
    t3 = <HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />;
    $[4] = closeDelay;
    $[5] = openDelay;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export type PromptInputHoverCardTriggerProps = ComponentProps<typeof HoverCardTrigger>;
export const PromptInputHoverCardTrigger = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HoverCardTrigger {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type PromptInputHoverCardContentProps = ComponentProps<typeof HoverCardContent>;
export const PromptInputHoverCardContent = t0 => {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      align: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const align = t1 === undefined ? "start" : t1;
  let t2;
  if ($[3] !== align || $[4] !== props) {
    t2 = <HoverCardContent align={align} {...props} />;
    $[3] = align;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export type PromptInputTabsListProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTabsList = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTabProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTab = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTabLabelProps = HTMLAttributes<HTMLHeadingElement>;
export const PromptInputTabLabel = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground mb-2 px-3 text-xs font-medium", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <h3 className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTabBodyProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTabBody = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("space-y-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputTabItemProps = HTMLAttributes<HTMLDivElement>;
export const PromptInputTabItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("hover:bg-accent flex items-center gap-2 px-3 py-2 text-xs", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandProps = ComponentProps<typeof Command>;
export const PromptInputCommand = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <Command className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandInputProps = ComponentProps<typeof CommandInput>;
export const PromptInputCommandInput = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandInput className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandListProps = ComponentProps<typeof CommandList>;
export const PromptInputCommandList = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandList className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandEmptyProps = ComponentProps<typeof CommandEmpty>;
export const PromptInputCommandEmpty = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandEmpty className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandGroupProps = ComponentProps<typeof CommandGroup>;
export const PromptInputCommandGroup = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandGroup className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandItemProps = ComponentProps<typeof CommandItem>;
export const PromptInputCommandItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type PromptInputCommandSeparatorProps = ComponentProps<typeof CommandSeparator>;
export const PromptInputCommandSeparator = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CommandSeparator className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp4(s) {
  return s.trim();
}
function _temp5(prev_1) {
  for (const file_2 of prev_1) {
    if (file_2.url) {
      URL.revokeObjectURL(file_2.url);
    }
  }
  return [];
}
function _temp6(e) {
  if (e.dataTransfer?.types.includes("Files")) {
    e.preventDefault();
  }
}
function _temp7(e_1) {
  if (e_1.dataTransfer?.types.includes("Files")) {
    e_1.preventDefault();
  }
}
async function _temp8(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}
function _temp9(item) {
  return {
    ...item,
    id: item.id
  };
}
function _temp10() {}
function _temp11() {}
function _temp12() {
  return typeof window !== "undefined" && "SpeechRecognition" in window;
}